import React, { useState, useEffect, useRef } from 'react';

interface CreateDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dealData: { purpose: string; amount: number; cardId: string }) => void;
  isLoading?: boolean;
  userCards: Array<{ id: string; maskedCardNumber: string }>;
}

const CreateDealModal: React.FC<CreateDealModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  userCards
}) => {
  const [step, setStep] = useState(1); // 1 - название, 2 - стоимость и карта
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedCardId, setSelectedCardId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Автоматический фокус на поле ввода
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (userCards.length > 0) {
      setSelectedCardId(userCards[0].id);
    }
  }, [userCards]);

  // Автофокус на поле суммы при переходе на второй шаг
  useEffect(() => {
    if (step === 2 && amountInputRef.current) {
      setTimeout(() => {
        amountInputRef.current?.focus();
      }, 100);
    }
  }, [step]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      if (purpose.trim()) {
        setStep(2);
      }
    } else if (step === 2) {
      if (purpose.trim() && amount && selectedCardId) {
        onSubmit({ purpose: purpose.trim(), amount: parseInt(amount), cardId: selectedCardId });
      }
    }
  };

  const handleClose = () => {
    setPurpose('');
    setAmount('');
    setSelectedCardId(userCards.length > 0 ? userCards[0].id : '');
    setStep(1);
    onClose();
  };

  // Функция форматирования PAN-карты в формате "1234 **** **** 1234"
  const formatCardNumber = (maskedCardNumber: string): string => {
    if (!maskedCardNumber) return '';
    
    // Убираем все нецифровые символы
    const cleaned = maskedCardNumber.replace(/\D/g, '');
    
    if (cleaned.length >= 8) {
      // Берем первые 4 и последние 4 цифры
      const firstFour = cleaned.slice(0, 4);
      const lastFour = cleaned.slice(-4);
      return `${firstFour} **** **** ${lastFour}`;
    }
    
    // Если цифр меньше 8, возвращаем как есть
    return maskedCardNumber;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#141826] rounded-t-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-white text-left p-4 relative border-b border-white/10">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors"
          >
            ✕
          </button>
          
          <h3 className="text-2xl text-left">Новая сделка</h3>
        </div>
        
        {/* Body */}
        <div className="text-white p-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Purpose Input */}
            {step === 1 && (
              <>
                <div>
                  <label className="block text-slate-300 text-sm mb-2">
                    Название сделки
                  </label>
                  <div className="relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#7538d5] text-lg"
                      placeholder="Придумайте название"
                      autoFocus
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  type="submit"
                  disabled={!purpose.trim()}
                  className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>Продолжить</span>
                </button>
              </>
            )}

            {/* Step 2: Amount and Card Selection */}
            {step === 2 && (
              <>
                {/* Amount Input */}
                <div>
                  <label className="block text-slate-300 text-sm mb-2">
                    Стоимость (₽)
                  </label>
                                     <input
                     ref={amountInputRef}
                     type="number"
                     value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-[#7538d5] text-lg"
                     placeholder="Введите сумму"
                     min="1"
                   />
                </div>

                {/* Card Selection (if multiple cards) */}
                {userCards.length > 1 && (
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">
                      Выберите карту для вывода
                    </label>
                    <select
                      value={selectedCardId}
                      onChange={(e) => setSelectedCardId(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-[#7538d5] text-lg"
                    >
                      {userCards.map(card => (
                        <option key={card.id} value={card.id}>
                          {formatCardNumber(card.maskedCardNumber)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Create Deal Button */}
                <button
                  type="submit"
                  disabled={!amount || !selectedCardId || isLoading}
                  className="w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 bg-white text-gray-800 hover:bg-white/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span>Создать сделку</span>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDealModal;
